import React, { useEffect } from 'react';
import { Images, X } from 'lucide-react';
import { IconButton } from '../../components/ui/PanelControls';
import { useUIState } from '../../state';
import AssetLibraryContent from './AssetLibraryContent';

/**
 * AssetLibraryDrawer — the global "fetched from anywhere" entry point into
 * the Asset Library, mounted once at the App.js root (sibling to the other
 * globally-triggered overlays: SettingsModal, ReportIssueModal,
 * AIFeedbackModal, GlobalSearchModal) and opened from any page via
 * `window.__openAssetLibrary(projectId)` -- the exact same convention
 * App.js already documents for those ("openable from anywhere via
 * window.__open...Modal()").
 *
 * Styled to match SourcesPanel.jsx/ArtifactPanel.jsx exactly (same colors,
 * 14px rounded card, same header padding/icon-button chrome, same subtle
 * shadow instead of a heavy backdrop scrim) -- floats as one more docked-
 * feeling panel in this app's family rather than a bespoke full-height
 * slide-in overlay.
 *
 * Reads isDarkMode from useUIState() directly (mounted inside
 * AppStateProvider -> UIStateProvider, same as every other themed panel)
 * instead of taking it as a prop from App.js's OWN local isDarkMode state --
 * that state only reads localStorage once at mount and is meant to
 * re-sync via a 'darkModeChanged' DOM event that nothing in this app
 * actually dispatches (confirmed: only ever listened for, in App.js/
 * NotesPage.jsx/OverviewPage.jsx, never fired) -- so it goes stale the
 * moment the real toggle (useUIState()'s own reducer) changes the theme
 * without this component's tree re-rendering for an unrelated reason.
 * This drawer render prop passed the isOpen/projectId/onClose it still
 * needs, but no longer trusts the caller for isDarkMode.
 */
const AssetLibraryDrawer = ({ isOpen, onClose, projectId }) => {
  const { state: uiState } = useUIState();
  const { isDarkMode } = uiState;
  const colors = {
    bg: isDarkMode ? '#0a0a0a' : '#f0ede5',
    border: isDarkMode ? '#1e1e1e' : '#e0ddd4',
    text: isDarkMode ? '#fafafa' : '#111',
    subtext: isDarkMode ? '#8a8a8a' : '#767676',
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, pointerEvents: 'none' }}>
      {/* Click-outside-to-close catcher -- no visible scrim, matching how
          this app's docked panels never dim the rest of the screen. */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }} />
      <div
        style={{
          position: 'absolute', top: '16px', right: '16px', bottom: '16px',
          width: 'min(640px, calc(100vw - 32px))', pointerEvents: 'auto',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          backgroundColor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: '14px',
          boxShadow: isDarkMode ? '0 8px 24px rgba(0,0,0,0.55)' : '0 8px 24px rgba(0,0,0,0.14)',
          fontFamily: 'Inter, sans-serif', color: colors.text,
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px', borderBottom: `1px solid ${colors.border}`, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Images size={15} color={colors.text} />
            <span style={{ fontSize: '13px', fontWeight: 700, color: colors.text }}>Asset Library</span>
          </div>
          <IconButton isDarkMode={isDarkMode} onClick={onClose} title="Close">
            <X size={15} />
          </IconButton>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }}>
          <AssetLibraryContent projectId={projectId} isDarkMode={isDarkMode} />
        </div>
      </div>
    </div>
  );
};

export default AssetLibraryDrawer;
